import { yupResolver } from "@hookform/resolvers/yup";
import { createContext, useEffect, useRef, useState } from "react";
import { useForm, useFormState } from "react-hook-form";
import { useOnBeforeUnload } from "../context/OnBeforeUnloadProvider";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";

export const FormContext = createContext(null);

export const Form = ({
  initialValues,
  validationSchema,
  prepareInitialValues,
  setIsDirty,
  children,
  onSubmit: originalOnSubmit,
  autoSubmit,
  onCancel,
  values,
  neverLock,
  ...props
}) => {
  const { reset, handleSubmit, control, ...propsForm } = useForm({
    resolver: validationSchema ? yupResolver(validationSchema) : undefined,
    mode: "all",
    defaultValues:
      initialValues || !values
        ? prepareInitialValues(initialValues)
        : undefined,
    values: values ? prepareInitialValues(values) : undefined,
  });
  const { isDirty, isValid, isSubmitSuccessful, ...propsFormState } =
    useFormState({ control });
  const { lock, unlock } = useOnBeforeUnload("Aie aie aie");
  const isValidRef = useRef(isValid);
  const isDirtyRef = useRef(isDirty);
  const nextSubmission = useRef(undefined);
  const autoSubmitRunning = useRef(false);
  const [warningOpen, setWarningOpen] = useState(false);

  useEffect(() => {
    setIsDirty && setIsDirty(isDirty);
    if (isDirty && !neverLock) lock();
    else unlock();

    isDirtyRef.current = isDirty;
    isValidRef.current = isValid;

    if (autoSubmit) {
      onSubmitAuto();
    }
  }, [isDirty, isValid]);

  useEffect(() => {
    // When form is destroyed, remove isDirty
    return () => {
      unlock();
      setIsDirty && setIsDirty(false);
      if (nextSubmission.current) {
        clearTimeout(nextSubmission.current);
      }
    };
  }, []);

  const onSubmitAuto = () => {
    if (autoSubmitRunning.current) return;

    if (isDirtyRef.current && isValidRef.current) {
      onSubmit();
      nextSubmission.current = setTimeout(() => {
        autoSubmitRunning.current = false;
        onSubmitAuto();
      }, 2000);
      autoSubmitRunning.current = true;
    } else {
      reset(undefined, {
        keepValues: true,
        keepIsSubmitSuccessful: true,
        keepIsSubmitted: true,
      });
    }
  };

  const onSubmit = handleSubmit(async (values) => {
    reset(undefined, { keepValues: true });
    originalOnSubmit(values);
  });

  const innerOnCancel = () => {
    if (!isDirty) onCancel();
    else setWarningOpen(true);
  };

  const forceCloseForm = () => {
    setIsDirty && setIsDirty(false);
    unlock();
    onCancel();
  };

  return (
    <FormContext.Provider
      value={{
        form: {
          formState: {
            isDirty,
            isValid,
            isSubmitSuccessful,
            ...propsFormState,
          },
          reset,
          handleSubmit,
          control,
          ...propsForm,
        },
        initialValues,
        values,
        onSubmit,
        prepareInitialValues,
        onCancel: innerOnCancel,
        ...props,
      }}
    >
      <Dialog
        open={warningOpen}
        onClose={() => setWarningOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{"Warning"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            You're form has unsaved changes, do you want to loose them ?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={forceCloseForm}>Discard</Button>
          <Button onClick={() => setWarningOpen(false)} autoFocus>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
      {children}
    </FormContext.Provider>
  );
};
